/**
 * Walk a dot-separated path from the leaf upward looking for the deepest
 * segment-prefix that satisfies `hasNode`. Tries the full path first, then
 * each ancestor in descending depth order.
 *
 * The caller decides what "has" means by passing a predicate — typically
 * `(p) => nodeMap.has(p)` for a Map<string, ...> or `(p) => idSet.has(p)`
 * for a Set. This keeps the algorithm independent of the lookup
 * structure used at the call site (Map for `findNavigableFormPath`,
 * Map-derived array for `getBreadcrumbsForProperty`).
 *
 * Returns `undefined` when no prefix — including the full path itself —
 * matches.
 *
 * @example
 *   findClosestAncestorPath('flowTemplate.campaignNodes.0.title', (p) => map.has(p))
 *     // -> 'flowTemplate' if `flowTemplate` is the deepest navigable ancestor
 */
export const findClosestAncestorPath = (
  formPath: string,
  hasNode: (path: string) => boolean,
): string | undefined => {
  if (hasNode(formPath)) return formPath;
  const segments = formPath.split('.');
  for (let length = segments.length - 1; length > 0; length -= 1) {
    const candidate = segments.slice(0, length).join('.');
    if (hasNode(candidate)) return candidate;
  }
  return undefined;
};
